/* Правило «без резких переходов» (APP-SPEC §1.4): то, что появилось при перерисовке на месте, входит через pop один раз;
   первая отрисовка раздела анимаций не запускает (раздел входит целиком — его pop делает каркас). */
import { createContext, useContext, useLayoutEffect, useRef, type RefObject } from 'react';
import { pop, tick } from './motion';

/** Раздел уже на экране: появления после этого анимируются */
export const LiveContext = createContext<RefObject<boolean>>({ current: false });

/** Поставить «раздел на экране» после первой отрисовки корня */
export function useLiveRoot(): RefObject<boolean> {
  const live = useRef(false);
  useLayoutEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  return live;
}

/** pop при появлении элемента, если раздел уже на экране */
export function usePopOnAppear<T extends Element>(): RefObject<T | null> {
  const ref = useRef<T>(null);
  const live = useContext(LiveContext);
  useLayoutEffect(() => {
    if (live.current) pop(ref.current);
  }, [live]);
  return ref;
}

/** tick, когда значение сменилось на месте */
export function useTickOnChange<T extends Element>(value: unknown): RefObject<T | null> {
  const ref = useRef<T>(null);
  const prev = useRef(value);
  useLayoutEffect(() => {
    if (prev.current !== value) tick(ref.current);
    prev.current = value;
  }, [value]);
  return ref;
}
